import { Component } from "react"

import i18n from "src/i18n"
import { currentBuildId } from "src/utils/serviceWorker"
import { clearClientState, forgetInstalledApp, recoverOnce } from "src/utils/recovery"

import "./CrashScreen.css"

/* The floor under the whole app.

   A render that throws with nothing to catch it unmounts every component React has, and the
   player is left with a black page: no words, no button, no way to know whether waiting helps.
   That is what the owner's "black screen after a few seconds in the lobby" was (2026-09-24) — a
   payload one view could not draw, and nothing between it and the page. This is the catch. It
   is a class because React gives error boundaries to classes only, and it reads `i18n` directly
   for the same reason: no hook.

   What it does is in `utils/recovery`: the first crash clears this browser's preferences and
   reloads once, the second shows the screen. Mounted outermost in `index.js`, outside the store
   and the router, so a crash in either of those is caught too. */
class CrashScreen extends Component {
  constructor(props) {
    super(props)
    this.state = { crashed: false }
  }

  static getDerivedStateFromError() {
    return { crashed: true }
  }

  componentDidCatch(error, info) {
    // The console is where the stack is still readable, and the only place it goes: the client
    // reports nothing, so a player's own devtools is the one witness.
    console.error("The app crashed while rendering", error, info?.componentStack)
    recoverOnce({ buildId: currentBuildId() })
  }

  reload = () => window.location.reload()

  startClean = async () => {
    clearClientState({ keepSession: false })
    await forgetInstalledApp()
    window.location.reload()
  }

  render() {
    if (!this.state.crashed) return this.props.children
    return (
      <div className="crash-screen" role="alert">
        <h1 className="crash-title">{i18n.t("app.crashTitle")}</h1>
        <p className="crash-body">{i18n.t("app.crashBody")}</p>
        <div className="crash-actions">
          <button type="button" onClick={this.reload}>
            {i18n.t("app.crashReload")}
          </button>
          <button type="button" className="quiet" onClick={this.startClean}>
            {i18n.t("app.crashReset")}
          </button>
        </div>
      </div>
    )
  }
}

export default CrashScreen
